import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { ModalProps } from "@/lib/type";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import {
  useAddRestaurantManagerMutation,
  useGetRestaurantsQuery,
} from "@/redux/services/restaurant/restaurant.api";
import { AddRestaurantManagerPayload } from "@/redux/services/restaurant/restaurant.type";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { cleanerError } from "@/lib/utils";

const schema = yup.object().shape({
  phone: yup.string().required().label("Téléphone"),
  fullName: yup.string().label("Nom complet"),
  restaurantId: yup.string().required().label("Restaurant"),
});

type FormData = AddRestaurantManagerPayload & { restaurantId: string };

type AddRestaurantManagerModalProps = ModalProps<void> & {
  // Preselected restaurant (owner), e.g. the one just created.
  restaurantId?: string;
};

const AddRestaurantManagerModal: React.FC<AddRestaurantManagerModalProps> = ({
  isOpen,
  onClose,
  restaurantId: preselectedRestaurantId,
}) => {
  const { currentRestaurant, currentUser } = useAppSelector((s) => s.user);
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const [addManager, { isLoading }] = useAddRestaurantManagerMutation();
  const { data: restaurants } = useGetRestaurantsQuery(undefined, { skip: !isOwner });
  const defaultRestaurantId =
    (isOwner ? preselectedRestaurantId : undefined) ??
    currentRestaurant?.id ??
    "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm<FormData>({
    // @ts-ignore
    resolver: yupResolver(schema),
    defaultValues: {
      restaurantId: defaultRestaurantId,
    },
  });

  // The owner's restaurant list may load (or refetch after a creation) after
  // the form mounts: re-apply the preselection once its option exists.
  useEffect(() => {
    if (defaultRestaurantId !== "") {
      setValue("restaurantId", defaultRestaurantId);
    }
  }, [restaurants, defaultRestaurantId, setValue]);

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const onSubmit = async (data: FormData) => {
    const { restaurantId, ...managerData } = data;
    const res = await addManager({
      restaurantId,
      data: managerData,
    });

    if ("data" in res) {
      toast.success("Manager ajouté avec succès");
      onClose();
    } else if ("error" in res) {
      toast.error("Une erreur est survenue");
    }
  };

  const restaurantOptions = isOwner
    ? (restaurants?.map((r) => ({
        label: r.name,
        value: r.id,
      })) ?? [])
    : currentRestaurant?.id
      ? [{ label: currentRestaurant.name, value: currentRestaurant.id }]
      : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader className="mb-8">
            <DialogTitle>Ajouter un manager</DialogTitle>

            <div>
              <Select
                label="Restaurant"
                id="restaurantId"
                placeholder="Choisir un restaurant"
                required
                options={restaurantOptions}
                {...register("restaurantId")}
                error={errors?.restaurantId}
                defaultValue={defaultRestaurantId}
                disabled={!isOwner}
              />
            </div>
            <div>
              <Input
                label="Téléphone"
                type="tel"
                id="phone"
                placeholder="Téléphone"
                required
                {...register("phone")}
                error={errors?.phone}
              />
            </div>
            <div>
              <Input
                label="Nom complet"
                type="text"
                id="fullName"
                placeholder="Nom complet (optionnel)"
                {...register("fullName")}
                error={errors?.fullName}
              />
            </div>
          </DialogHeader>

          <DialogFooter>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              Ajouter
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRestaurantManagerModal;
